import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ThumbsDownIcon,
  ThumbsUpIcon
} from "@hugeicons-pro/core-stroke-rounded";

import {
  ThumbsDownIcon as ThumbsDownSolidIcon,
  ThumbsUpIcon as ThumbsUpSolidIcon
} from "@hugeicons-pro/core-solid-rounded";

import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Popover, PopoverTrigger } from "shared/components/Popover";
import { PopoverContent } from "shared/components/Popover";
import { TextArea } from "shared/components";
import { Button, ButtonSize, ButtonVariant } from "shared/components";

import { instance } from "api/apiBase";
import { Feedback, FeedbackSubmission, FeedbackType } from "../types";

interface FeedbackButtonsProps {
  summaryId: number;
  applicationId: number;
  feedback?: Feedback;
}

export const FeedbackButtons = ({
  summaryId,
  applicationId,
  feedback
}: FeedbackButtonsProps) => {
  const queryClient = useQueryClient();
  const [localFeedback, setLocalFeedback] = useState<FeedbackType | null>(
    feedback?.feedback_type || null
  );
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");

  useEffect(() => {
    setLocalFeedback(feedback?.feedback_type || null);
  }, [feedback?.feedback_type]);

  const submitFeedbackMutation = useMutation({
    mutationFn: (data: FeedbackSubmission) =>
      instance
        .post(
          `/portal/v1/applications/${applicationId}/application_intelligence/feedback`,
          data
        )
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION_INTELLIGENCE_FEEDBACK", applicationId]
      });
      setPopoverOpen(false);
      setFeedbackText("");
    }
  });

  const handleThumbsUp = () => {
    setLocalFeedback("thumbs_up");
    submitFeedbackMutation.mutate({
      summary_id: summaryId,
      feedback_type: "thumbs_up"
    });
  };

  const handleThumbsDownSubmit = () => {
    setLocalFeedback("thumbs_down");
    submitFeedbackMutation.mutate({
      summary_id: summaryId,
      feedback_type: "thumbs_down",
      feedback_text: feedbackText || undefined
    });
  };

  const clearFeedback = () => {
    setLocalFeedback(null);
    setFeedbackText("");
    submitFeedbackMutation.mutate({
      summary_id: summaryId,
      feedback_type: null
    });
  };

  return (
    <div className="flex gap-2 items-center">
      <p className="text-sm font-medium text-content-1000">Was this helpful?</p>

      <IconButton
        variant={IconButtonVariant.TEXT}
        size={IconButtonSize.SM}
        HugeIcon={
          localFeedback === "thumbs_up" ? ThumbsUpSolidIcon : ThumbsUpIcon
        }
        strokeWidth={localFeedback === "thumbs_up" ? 1 : undefined}
        onClick={localFeedback === "thumbs_up" ? clearFeedback : handleThumbsUp}
        disabled={submitFeedbackMutation.isPending}
        className="!text-content-200 hover:!text-content-700 hover:-rotate-12 hover:scale-110"
      />

      {localFeedback === "thumbs_down" ? (
        <IconButton
          variant={IconButtonVariant.TEXT}
          size={IconButtonSize.SM}
          HugeIcon={ThumbsDownSolidIcon}
          onClick={clearFeedback}
          strokeWidth={1}
          className="!text-content-200 hover:!text-content-100 hover:rotate-6 hover:scale-110"
        />
      ) : (
        <Popover
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          placement="bottom-end"
        >
          <PopoverTrigger
            asChild
            disabled={submitFeedbackMutation.isPending}
            as="div"
          >
            <IconButton
              variant={IconButtonVariant.TEXT}
              size={IconButtonSize.SM}
              HugeIcon={ThumbsDownIcon}
              onClick={() => {
                setPopoverOpen(true);
              }}
              className="!text-content-200 hover:!text-content-700 hover:rotate-6 hover:scale-110"
            />
          </PopoverTrigger>
          <PopoverContent className="bg-white border z-[1] border-alpha-100 rounded-lg shadow-lg p-4 max-w-[540px] w-full">
            <div className="space-y-3">
              <div>
                <TextArea
                  borderLess
                  id="feedback-text"
                  placeholder="Tell us what you didn't like"
                  value={feedbackText}
                  autoFocus
                  className="max-h-20 overflow-y-auto !min-h-5 placeholder:text-sm placeholder:!font-medium placeholder:text-content-300 resize-none text-sm focus:shadow-none !shadow-none placeholder:not-italic"
                  onChange={(e) => setFeedbackText(e.target.value)}
                  rows={1}
                  autoExpand
                  legacyErrorMargins={false}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  onClick={() => {
                    setPopoverOpen(false);
                    setFeedbackText("");
                  }}
                >
                  No thanks
                </Button>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  disabled={feedbackText.trim().length < 2}
                  onClick={handleThumbsDownSubmit}
                  loading={submitFeedbackMutation.isPending}
                >
                  Send it
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
